import {
	DatePicker as FriskDatePicker,
} from '@hdir/ui/date-picker';
import { isStringLiteral } from '@enonic/js-utils/value/isStringLiteral';
import cx, { type ClassValue } from 'clsx';
import React, {
	type FocusEventHandler,
	useEffect, useId, useRef, useState } from 'react';
import {
	type FieldPath,
	type FieldValues,
	type UseControllerProps,
	useController,
} from 'react-hook-form';
import {
	type PaddingProp,
	paddingCn,
} from '../../tailwind/padding';
import { useFormContext } from './FormContext';


interface ODFDatePickerProps<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> {
	className?: ClassValue
	disabled?: boolean;
	errorSummaryLabel?: string;
	fullWidth?: boolean;
	hideLabel?: boolean;
	infoText?: string
	label?: string;
	name: TName;
	onBlur?: FocusEventHandler & FocusEventHandler<HTMLLabelElement>;
	padding?: PaddingProp;
	rules?: UseControllerProps<TFieldValues, TName>['rules'];
}


export function DatePicker<
	TFieldValues extends FieldValues = FieldValues,
	TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
	className,
	disabled: disabledProp,
	fullWidth,
	hideLabel,
	infoText,
	label,
	errorSummaryLabel = isStringLiteral(label) ? label : undefined,
	name,
	onBlur,
	padding,
	rules,
	...otherProps
}: ODFDatePickerProps<TFieldValues, TName>) : React.JSX.Element {
	const {
		control,
		getRequired,
		setErrorSummaryLabel,
		setFocusRef,
		setScrollRef,
	} = useFormContext<TFieldValues>();

	const id = useId(); // Labels the DatePicker-wrapper

	// If an input should be visually marked as required, one should also use validation.
	// Therefore validation rules should determine the visual.
	// Setting rules from visual is possible, but allowing both ways would be confusing.
	// Validation can be passed directly via rules, or via useForm resolvers.
	const required: boolean = rules
		? !!rules.required
		: getRequired
			? getRequired(name)
			: false;

	const {
		field: {
			disabled: useControllerDisabled,
			onBlur: useControllerOnBlur,
			onChange: useControllerOnChange,
			value,
		},
		fieldState: {
			error,
		},
		// This can cause: Warning: Cannot update a component (`Form`) while rendering a different component (`DatePicker`).
		// If placed after return logic like if (!hydrated)
	} = useController<TFieldValues, TName>({
		control,
		disabled: disabledProp,
		name,
	});
	const containerRef = useRef<HTMLDivElement>();
	const [hydrated, setHydrated] = useState(false);
	const disabled = /* !hydrated || */ useControllerDisabled;

	useEffect(() => { // NOTE: useEffect is part of logic and MUST come before any return statement!
		setHydrated(true);
	}, []);

	useEffect(() => { // NOTE: useEffect is part of logic and MUST come before any return statement!
		if (errorSummaryLabel) {
			setErrorSummaryLabel({field: name, label: errorSummaryLabel});
		}
	},[errorSummaryLabel]);

	useEffect(() => {
		if (containerRef.current) {
			setFocusRef({ field: name, el: containerRef.current });
			if (!hideLabel && isStringLiteral(label)) {
				setScrollRef({ field: name, el: containerRef.current });
			}
		}
	}, [label, containerRef.current]);

	if (typeof document === 'undefined' || !hydrated) return (
		<FriskDatePicker
			className={cx(
				className,
				paddingCn(padding),
			)}
			fullWidth={fullWidth}
			hideLabel={hideLabel}
			infoText={infoText}
			label={label}
			required={required}
			value={value}
		/>
	);

	return (
		<div
			ref={(divElOrNull) => {
				if (divElOrNull) {
					containerRef.current = divElOrNull;
				}
			}}
		>
			<FriskDatePicker
				{...otherProps}
				className={cx(
					className,
					paddingCn(padding),
				)}

				// NOTE: This will always be a controlled component.
				// So defaultValue shouldn't be supported.
				// value will contain parent form defaultValues[name].

				error={error ? error.message : null}
				fullWidth={fullWidth}
				hideLabel={hideLabel}
				infoText={infoText}
				label={label}

				// NOTE: Neither FriskDatePicker nor it's underlying @react-statly/datepicker have a name property.
				// There is no input type='date' in the final html, so name is only used in React state handling.

				onBlur={(e) => {
					// https://react-hook-form.com/docs/usecontroller
					// A function which sends the input's onBlur event to the library.
					// It should be assigned to the input's onBlur prop.
					useControllerOnBlur();

					if (onBlur) onBlur(e);
				}}

				onChange={(date: Date | null) => {
					useControllerOnChange(date);
				}}
				required={required}
				value={value}
			/>
		</div>
	);
}
